import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Search,
  UserPlus,
  Edit,
  Trash2,
  Key,
  Mail,
  Phone,
  Crown,
  X,
  Eye,
  EyeOff,
  ChevronDown,
  Filter,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  User,
  Calendar,
  ArrowLeft,
  Sparkles,
  UserCheck,
  RotateCcw,
  Database,
  FolderPlus
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { ImpersonateButton } from "../../components/ImpersonationBanner";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ConvertTrialUserModal from "../../components/Admin/ConvertTrialUserModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Edit User Modal
const EditUserModal = ({ user, isOpen, onClose, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    plan_type: "estandar"
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        plan_type: user.plan_type || "estandar"
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Nombre y email son requeridos");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/admin/users/${user.id}`, formData);
      toast.success("Usuario actualizado exitosamente");
      onUserUpdated(); // Just notify to refresh
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar usuario");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4"
      >
        <div className="flex items-center justify-between p-6 border-b border-navy-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Edit className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-navy-900">Editar Usuario</h2>
              <p className="text-sm text-navy-500">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-navy-50 rounded-lg" data-testid="close-edit-modal">
            <X className="w-5 h-5 text-navy-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Nombre completo</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="h-11"
              data-testid="edit-user-name"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 h-11"
                data-testid="edit-user-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Teléfono</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                value={formData.phone || ""}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="pl-10 h-11"
                data-testid="edit-user-phone"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Plan</Label>
            <Select value={formData.plan_type} onValueChange={(v) => setFormData({ ...formData, plan_type: v })}>
              <SelectTrigger className="h-11" data-testid="edit-user-plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="estandar">Estándar</SelectItem>
                <SelectItem value="premium">⭐ Premium</SelectItem>
                <SelectItem value="vip">👑 VIP</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" disabled={loading} data-testid="edit-user-submit">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Guardar Cambios"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Reset Password Modal
const ResetPasswordModal = ({ user, isOpen, onClose }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let pwd = "";
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(pwd);
    setShowPassword(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/admin/users/${user.id}/change-password`, { 
        new_password: password || null, // null = generate random
        send_email: sendEmail 
      });
      
      setResult({
        success: true,
        password: response.data.new_password,
        email_sent: response.data.email_sent
      });
      
      toast.success(`Contraseña actualizada para ${user.name}`, {
        description: sendEmail ? "Email enviado con las credenciales" : "Sin notificación por email"
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar contraseña");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword("");
    setResult(null);
    setSendEmail(true);
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Contraseña copiada al portapapeles");
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4"
      >
        <div className="flex items-center justify-between p-6 border-b border-navy-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Key className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-navy-900">Cambiar Contraseña</h2>
              <p className="text-sm text-navy-500">{user.name} • {user.email}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-navy-50 rounded-lg" data-testid="close-reset-modal">
            <X className="w-5 h-5 text-navy-400" />
          </button>
        </div>

        {result ? (
          // Success Result View
          <div className="p-6 space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="font-semibold text-emerald-800 mb-1">¡Contraseña Actualizada!</h3>
              <p className="text-sm text-emerald-600">
                {result.email_sent 
                  ? "Se envió un email al usuario con la nueva contraseña"
                  : "Contraseña actualizada sin notificación por email"}
              </p>
            </div>

            <div className="bg-navy-50 rounded-xl p-4">
              <p className="text-xs text-navy-500 mb-2">Nueva contraseña:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-4 py-3 rounded-lg font-mono text-lg text-navy-900 border border-navy-200 text-center">
                  {result.password}
                </code>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(result.password)}
                  className="shrink-0"
                >
                  Copiar
                </Button>
              </div>
            </div>

            <Button onClick={handleClose} className="w-full bg-navy-800 hover:bg-navy-900">
              Cerrar
            </Button>
          </div>
        ) : (
          // Form View
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-navy-700 font-medium">Nueva contraseña</Label>
                <button type="button" onClick={generatePassword} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                  Generar automática
                </button>
              </div>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Dejar vacío para generar automática"
                  className="pl-10 pr-10 h-11"
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-navy-500">Mínimo 6 caracteres o dejar vacío para generar una automáticamente</p>
            </div>

            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Enviar por email</p>
                  <p className="text-xs text-blue-600">Notificar al usuario la nueva contraseña</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={sendEmail} 
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" disabled={loading} data-testid="reset-password-submit">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Cambiar Contraseña"}
              </Button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteUserModal = ({ user, isOpen, onClose, onUserDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [deletedData, setDeletedData] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const handleDelete = async () => {
    if (confirmText !== "ELIMINAR") {
      toast.error("Escribe ELIMINAR para confirmar");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.delete(`${API}/admin/users/${user.id}`);
      setDeletedData(response.data.deleted_data);
      toast.success(response.data.message || "Usuario eliminado permanentemente");
      setTimeout(() => {
        onUserDeleted(user.id);
        onClose();
        setConfirmText("");
        setDeletedData(null);
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4"
      >
        {!deletedData ? (
          <div className="p-6">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-heading text-xl font-bold text-navy-900 text-center mb-2">
              Eliminación Permanente
            </h2>
            <p className="text-navy-500 text-center mb-4">
              Esta acción eliminará a <strong className="text-red-600">{user.name}</strong> y <strong className="text-red-600">TODOS</strong> sus datos del sistema:
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Perfil de usuario</li>
                <li>• Casos asignados</li>
                <li>• Conversaciones y mensajes</li>
                <li>• Documentos subidos</li>
                <li>• Análisis estratégicos</li>
                <li>• Pagos y transacciones</li>
                <li>• Citas y reuniones</li>
                <li>• Notificaciones</li>
              </ul>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-800 font-medium text-center">
                Esta acción es IRREVERSIBLE. El usuario nunca más aparecerá en el sistema.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="text-sm font-medium text-navy-700 block mb-2">
                Escribe <strong className="text-red-600">ELIMINAR</strong> para confirmar:
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="ELIMINAR"
                className="text-center font-bold"
                data-testid="confirm-delete-text"
              />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { onClose(); setConfirmText(""); }} className="flex-1" disabled={loading}>
                Cancelar
              </Button>
              <Button 
                onClick={handleDelete} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white" 
                disabled={loading || confirmText !== "ELIMINAR"} 
                data-testid="confirm-delete-user"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Permanentemente
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="font-heading text-xl font-bold text-navy-900 text-center mb-2">
              Usuario Eliminado
            </h2>
            <p className="text-navy-500 text-center mb-4">
              {user.name} ha sido eliminado permanentemente.
            </p>
            <div className="bg-navy-50 rounded-xl p-4">
              <p className="text-sm font-medium text-navy-700 mb-2">Datos eliminados:</p>
              <div className="flex flex-wrap gap-2">
                {deletedData?.map((item, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white text-navy-600 text-xs rounded-lg border border-navy-200">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// User Row Component
const UserRow = ({ user, onEdit, onResetPassword, onDelete, onConvertTrial, onResetCase, onCreateCase }) => {
  const isTrial = user.plan_type === "prueba" || user.is_trial === true;
  const hasActiveCase = user.has_active_case === true;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white border rounded-xl p-4 hover:shadow-md transition-shadow ${
        isTrial ? "border-amber-200 bg-amber-50/30" : "border-navy-100"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
            user.role === "admin" ? "bg-purple-100 text-purple-700" : 
            isTrial ? "bg-amber-100 text-amber-700" : "bg-navy-100 text-navy-600"
          }`}>
            {user.name?.charAt(0).toUpperCase() || "?"}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-navy-900">{user.name}</h3>
              {/* Case Status Badge - For clients */}
              {user.role === "client" && (
                hasActiveCase ? (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1 border border-green-200" title={`Caso: ${user.active_case?.plan || 'N/A'} | Oficial: ${user.active_case?.oficial || 'Sin asignar'}`}>
                    <CheckCircle2 className="w-3 h-3" /> Con Caso
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full flex items-center gap-1 border border-red-200">
                    <AlertTriangle className="w-3 h-3" /> Sin Caso
                  </span>
                )
              )}
              {/* Trial Badge - Priority */}
              {user.role === "client" && isTrial && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1 border border-amber-200">
                  <Sparkles className="w-3 h-3" /> Prueba
                </span>
              )}
              {/* Plan badges for non-trial clients */}
              {user.role === "client" && !isTrial && user.plan_type === "vip" && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Crown className="w-3 h-3" /> VIP
                </span>
              )}
              {user.role === "client" && !isTrial && user.plan_type === "premium" && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                  ⭐ Premium
                </span>
              )}
              {user.role === "client" && !isTrial && (!user.plan_type || user.plan_type === "estandar") && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                  Estándar
                </span>
              )}
              {user.role === "admin" && (
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                  Admin
                </span>
              )}
              {user.role === "vendedor" && (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                  Vendedor
                </span>
              )}
              {/* REMOVED: oficial badge - role eliminated */}
            </div>
            <div className="flex items-center gap-4 text-sm text-navy-500 mt-1">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" /> {user.email}
              </span>
              {user.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {user.phone}
                </span>
              )}
              {/* Show case info if exists */}
              {user.role === "client" && hasActiveCase && user.active_case && (
                <span className="flex items-center gap-1 text-green-600">
                  <FolderPlus className="w-3 h-3" /> {user.active_case.plan?.toUpperCase()}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* REMOVED: Convert Trial Button - Trial system eliminated */}
          {/* Impersonate Button - For clients and vendedores */}
          {(user.role === "client" || user.role === "vendedor") && (
            <ImpersonateButton 
              userId={user.id} 
              userName={user.name} 
              userRole={user.role}
            />
          )}
          {/* Create Case Button - For clients without active case */}
          {user.role === "client" && !hasActiveCase && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onCreateCase(user)} 
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid={`create-case-${user.id}`}
              title="Crear caso para este cliente"
            >
              <FolderPlus className="w-4 h-4 mr-1" />
              Crear Caso
            </Button>
          )}
          {/* View Case Button - For clients with active case */}
          {user.role === "client" && hasActiveCase && user.active_case && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.open(`/admin/cases/${user.active_case.id}`, '_blank')}
              className="text-green-600 border-green-200 hover:bg-green-50"
              data-testid={`view-case-${user.id}`}
              title="Ver caso activo"
            >
              <Eye className="w-4 h-4 mr-1" />
              Ver Caso
            </Button>
          )}
          {user.role !== "admin" && (
            <>
              <Button variant="ghost" size="sm" onClick={() => onEdit(user)} data-testid={`edit-user-${user.id}`} title="Editar usuario">
                <Edit className="w-4 h-4 text-blue-600" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onResetPassword(user)} data-testid={`reset-pwd-${user.id}`} title="Cambiar contraseña">
                <Key className="w-4 h-4 text-amber-600" />
              </Button>
              {user.role === "client" && (
                <Button variant="ghost" size="sm" onClick={() => onResetCase(user)} data-testid={`reset-case-${user.id}`} title="Reiniciar caso">
                  <RotateCcw className="w-4 h-4 text-purple-600" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => onDelete(user)} data-testid={`delete-user-${user.id}`} title="Eliminar usuario">
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Main Component
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterCase, setFilterCase] = useState("all"); // all, with_case, without_case
  
  const [editUser, setEditUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [convertTrialUser, setConvertTrialUser] = useState(null);
  const [resetCaseUser, setResetCaseUser] = useState(null);
  const [createCaseUser, setCreateCaseUser] = useState(null);
  const [createCaseData, setCreateCaseData] = useState({ plan: "vip", num_people: 1, payment_status: "pagado" });
  const [creatingCase, setCreatingCase] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateTo6Steps = async () => {
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-to-6-steps`);
      const { migrated, already_migrated, errors } = response.data;
      
      toast.success(`Migración completada`, {
        description: `${migrated} casos actualizados, ${already_migrated} ya estaban en 5 pasos`
      });
      
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} errores durante la migración`);
      }
      
      setShowMigrationModal(false);
      fetchUsers(); // Refresh users list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error durante la migración");
    } finally {
      setMigrating(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!user.name?.toLowerCase().includes(query) && !user.email?.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (filterRole !== "all" && user.role !== filterRole) return false;
      if (filterPlan !== "all" && user.plan_type !== filterPlan) return false;
      
      // Filter by case status
      if (filterCase === "without_case" && user.role === "client" && user.has_active_case !== false) return false;
      if (filterCase === "with_case" && user.role === "client" && user.has_active_case !== true) return false;
      // If filtering by case status, only show clients
      if (filterCase !== "all" && user.role !== "client") return false;
      
      return true;
    });
  }, [users, searchQuery, filterRole, filterPlan, filterCase]);

  const stats = useMemo(() => ({
    total: users.length,
    clients: users.filter(u => u.role === "client").length,
    vendedores: users.filter(u => u.role === "vendedor").length,
    // REMOVED: oficiales stat - role eliminated
    admins: users.filter(u => u.role === "admin").length,
    vip: users.filter(u => u.plan_type === "vip").length,
    premium: users.filter(u => u.plan_type === "premium").length,
    // REMOVED: prueba stat - trial system eliminated
    withCase: users.filter(u => u.role === "client" && u.has_active_case === true).length,
    withoutCase: users.filter(u => u.role === "client" && u.has_active_case === false).length
  }), [users]);

  const handleUserUpdated = () => {
    fetchUsers(); // Refresh the entire list
  };

  const handleUserDeleted = async (userId) => {
    // Refetch data to ensure consistency (avoid UI freeze from state mutation)
    await fetchUsers();
  };

  const handleCleanupDuplicateAdmins = async () => {
    if (!window.confirm("¿Estás seguro de eliminar admins duplicados? Solo se mantendrá uno por email.")) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/cleanup-duplicate-admins`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchUsers(); // Refresh the list
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al limpiar duplicados");
    }
  };

  const handleResetCase = async () => {
    if (!resetCaseUser) return;
    
    try {
      const response = await axios.post(`${API}/admin/users/${resetCaseUser.id}/reset-case`);
      toast.success(response.data.message, {
        description: `Casos eliminados: ${response.data.deleted_counts.cases}, Pasos eliminados: ${response.data.deleted_counts.steps}`
      });
      setResetCaseUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reiniciar caso");
    }
  };

  const handleCreateCase = async () => {
    if (!createCaseUser) return;
    
    setCreatingCase(true);
    try {
      const response = await axios.post(`${API}/case-management/create-case`, {
        client_email: createCaseUser.email,
        plan: createCaseData.plan,
        num_people: createCaseData.num_people,
        payment_status: createCaseData.payment_status
      });
      toast.success(response.data.message, {
        description: `Oficial asignado: ${response.data.assigned_oficial}`
      });
      setCreateCaseUser(null);
      setCreateCaseData({ plan: "vip", num_people: 1, payment_status: "pagado" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear caso");
    } finally {
      setCreatingCase(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to="/admin" className="p-2 hover:bg-navy-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-navy-500" />
              </Link>
              <h1 className="font-heading text-3xl font-bold text-navy-900">Gestión de Usuarios</h1>
            </div>
            <p className="text-navy-500 ml-12">Administra los usuarios de la plataforma</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowMigrationModal(true)} variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Database className="w-4 h-4 mr-2" />
              Migrar a 6 Pasos
            </Button>
            <Button onClick={fetchUsers} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <p className="text-sm text-navy-500">Total Usuarios</p>
            <p className="text-2xl font-bold text-navy-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <p className="text-sm text-navy-500">Clientes</p>
            <p className="text-2xl font-bold text-navy-900">{stats.clients}</p>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 p-4 relative">
            <p className="text-sm text-amber-600 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> En Prueba
            </p>
            <p className="text-2xl font-bold text-amber-600">{stats.prueba}</p>
            {stats.prueba > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
            )}
          </div>
          <div className={`bg-white rounded-xl border p-4 relative ${stats.withoutCase > 0 ? 'border-red-300' : 'border-navy-100'}`}>
            <p className={`text-sm flex items-center gap-1 ${stats.withoutCase > 0 ? 'text-red-600' : 'text-navy-500'}`}>
              <AlertTriangle className="w-3 h-3" /> Sin Caso
            </p>
            <p className={`text-2xl font-bold ${stats.withoutCase > 0 ? 'text-red-600' : 'text-navy-400'}`}>{stats.withoutCase}</p>
            {stats.withoutCase > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            )}
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4 relative">
            <p className="text-sm text-navy-500">Administradores</p>
            <p className="text-2xl font-bold text-purple-600">{stats.admins}</p>
            {stats.admins > 1 && (
              <button
                onClick={handleCleanupDuplicateAdmins}
                className="absolute top-2 right-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                title="Eliminar admins duplicados"
              >
                Limpiar
              </button>
            )}
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <p className="text-sm text-navy-500">Premium/VIP</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.premium + stats.vip}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="users-search"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-40" data-testid="filter-role">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="client">Clientes</SelectItem>
                <SelectItem value="vendedor">Vendedores</SelectItem>
                {/* REMOVED: oficial filter - role eliminated */}
                <SelectItem value="admin">Admins</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPlan} onValueChange={setFilterPlan}>
              <SelectTrigger className="w-40" data-testid="filter-plan">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los planes</SelectItem>
                {/* REMOVED: prueba filter - trial eliminated */}
                <SelectItem value="vip">VIP</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="estandar">Estándar</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCase} onValueChange={setFilterCase}>
              <SelectTrigger className={`w-44 ${filterCase === 'without_case' ? 'border-red-300 bg-red-50' : ''}`} data-testid="filter-case">
                <SelectValue placeholder="Estado caso" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="without_case">🔴 Sin Caso</SelectItem>
                <SelectItem value="with_case">✅ Con Caso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Quick filter buttons */}
          {stats.withoutCase > 0 && (
            <div className="mt-3 pt-3 border-t border-navy-100">
              <Button
                variant={filterCase === "without_case" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterCase(filterCase === "without_case" ? "all" : "without_case")}
                className={filterCase === "without_case" ? "bg-red-600 hover:bg-red-700" : "text-red-600 border-red-200 hover:bg-red-50"}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Ver {stats.withoutCase} clientes sin caso
              </Button>
            </div>
          )}
        </div>

        {/* Users List */}
        <div className="space-y-3">
          <AnimatePresence>
            {filteredUsers.map(user => (
              <UserRow
                key={user.id}
                user={user}
                onEdit={setEditUser}
                onResetPassword={setResetPasswordUser}
                onDelete={setDeleteUser}
                onConvertTrial={setConvertTrialUser}
                onResetCase={setResetCaseUser}
                onCreateCase={setCreateCaseUser}
              />
            ))}
          </AnimatePresence>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-navy-100">
              <User className="w-12 h-12 text-navy-300 mx-auto mb-4" />
              <p className="text-navy-500">No se encontraron usuarios</p>
            </div>
          )}
        </div>

        {/* Modals */}
        <EditUserModal
          user={editUser}
          isOpen={!!editUser}
          onClose={() => setEditUser(null)}
          onUserUpdated={handleUserUpdated}
        />
        <ResetPasswordModal
          user={resetPasswordUser}
          isOpen={!!resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
        />
        <DeleteUserModal
          user={deleteUser}
          isOpen={!!deleteUser}
          onClose={() => setDeleteUser(null)}
          onUserDeleted={handleUserDeleted}
        />
        <ConvertTrialUserModal
          user={convertTrialUser}
          isOpen={!!convertTrialUser}
          onClose={() => setConvertTrialUser(null)}
          onUserConverted={handleUserUpdated}
        />

        {/* Reset Case Confirmation Modal */}
        {resetCaseUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4"
            >
              <div className="flex items-center gap-3 p-6 border-b border-navy-100">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <RotateCcw className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-navy-900">Reiniciar Caso</h2>
                  <p className="text-sm text-navy-500">{resetCaseUser.name}</p>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Esta acción eliminará:</p>
                      <ul className="text-sm text-amber-700 mt-2 space-y-1">
                        <li>• Todos los casos del usuario</li>
                        <li>• Todos los pasos del Camino al Éxito</li>
                        <li>• Análisis estratégico y notas</li>
                        <li>• Solicitudes de documentos</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm text-emerald-800">
                    <CheckCircle2 className="w-4 h-4 inline mr-2" />
                    El usuario podrá comenzar un <strong>nuevo Camino al Éxito con 6 pasos</strong>.
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setResetCaseUser(null)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button onClick={handleResetCase} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" data-testid="confirm-reset-case">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reiniciar Caso
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Migration Modal */}
        {showMigrationModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !migrating && setShowMigrationModal(false)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-navy-900 flex items-center gap-2">
                  <Database className="w-6 h-6 text-amber-600" />
                  Migrar a 6 Pasos
                </h2>
                <button 
                  onClick={() => !migrating && setShowMigrationModal(false)}
                  className="p-2 hover:bg-navy-100 rounded-lg"
                  disabled={migrating}
                >
                  <X className="w-5 h-5 text-navy-500" />
                </button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 mb-1">Esta acción:</p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• Convertirá todos los casos antiguos a 5 pasos</li>
                      <li>• Eliminará los pasos 7, 8 y 9 de cada caso</li>
                      <li>• Recalculará el progreso de cada cliente</li>
                      <li>• Es irreversible</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-emerald-800">
                  <CheckCircle2 className="w-4 h-4 inline mr-2" />
                  Los nuevos casos ya se crean con 5 pasos automáticamente. Esta migración es solo para casos existentes.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowMigrationModal(false)}
                  className="flex-1"
                  disabled={migrating}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleMigrateTo6Steps}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  disabled={migrating}
                >
                  {migrating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Migrando...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Ejecutar Migración
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Create Case Modal */}
        {createCaseUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4"
            >
              <div className="flex items-center gap-3 p-6 border-b border-navy-100">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FolderPlus className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-navy-900">Crear Caso</h2>
                  <p className="text-sm text-navy-500">{createCaseUser.name} • {createCaseUser.email}</p>
                </div>
                <button 
                  onClick={() => setCreateCaseUser(null)} 
                  className="ml-auto p-2 hover:bg-navy-50 rounded-lg"
                  disabled={creatingCase}
                >
                  <X className="w-5 h-5 text-navy-400" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-navy-700">Plan</label>
                  <Select 
                    value={createCaseData.plan} 
                    onValueChange={(v) => setCreateCaseData({...createCaseData, plan: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vip">👑 VIP Enterprise</SelectItem>
                      <SelectItem value="premium">⭐ Premium</SelectItem>
                      <SelectItem value="estandar">Estándar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-navy-700">Número de Personas</label>
                  <Select 
                    value={createCaseData.num_people.toString()} 
                    onValueChange={(v) => setCreateCaseData({...createCaseData, num_people: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 persona</SelectItem>
                      <SelectItem value="2">2 personas</SelectItem>
                      <SelectItem value="3">3 personas</SelectItem>
                      <SelectItem value="4">4 personas</SelectItem>
                      <SelectItem value="5">5 personas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-navy-700">Estado de Pago</label>
                  <Select 
                    value={createCaseData.payment_status} 
                    onValueChange={(v) => setCreateCaseData({...createCaseData, payment_status: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pagado">✅ Pagado</SelectItem>
                      <SelectItem value="pendiente">⏳ Pendiente</SelectItem>
                      <SelectItem value="cortesia">🎁 Cortesía</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <CheckCircle2 className="w-4 h-4 inline mr-2" />
                    Se asignará automáticamente al oficial con menos casos activos.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setCreateCaseUser(null)} 
                    className="flex-1" 
                    disabled={creatingCase}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleCreateCase} 
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" 
                    disabled={creatingCase}
                    data-testid="confirm-create-case"
                  >
                    {creatingCase ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <FolderPlus className="w-4 h-4 mr-2" />
                        Crear Caso
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminUsers;
